import * as React from 'react';
import { Box, styled } from '@mui/system';
import { StyledBaseInput, StyledError } from './styles';
import useWindowSize from '@tphglobal/common/hooks/windowSize';
import { breakpoints } from '@tphglobal/common/theme/style.layout';

interface Props {
  value?: string;
  onChange?: (value: any) => void;
  error: string;
  length?: number;
  otp : string,
  setOtp : (otp : string) => void;
  onEnter?: () => void;
}

const OTP = ({
  length,
  value,
  onChange,
  onEnter,
}: {
  length: number;
  value: any;
  onChange: any;
  onEnter?: () => void;
}) => {
  const { width } = useWindowSize();
  const isMobile = width <= breakpoints.mobile;
  const inputRefs = React.useRef<HTMLInputElement[]>(
    new Array(length).fill(null)
  );

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case 'Delete':
        event.preventDefault();
        onChange((prevOtp: any) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case 'Backspace':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange((prevOtp: any) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      case 'Enter':
        event.preventDefault();
        if (currentIndex === length - 1 && value.length === length) {
          onEnter?.();
        }
        break;
      default:
        break;
    }
  };

  const handleChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  currentIndex: number
) => {
  const currentValue = event.target.value;

  // Handle keyboard clipboard / OTP autofill
  if (currentValue.length > 1 && value.length <= 1) {
    const sanitized = currentValue.replace(/\D/g, "").slice(0, length);
    onChange(sanitized);

    const focusIndex = Math.min(sanitized.length - 1, length - 1);
    focusInput(focusIndex);
    return;
  }

  let indexToEnter = 0;

  while (indexToEnter <= currentIndex) {
    if (
      inputRefs.current[indexToEnter].value &&
      indexToEnter < currentIndex
    ) {
      indexToEnter += 1;
    } else {
      break;
    }
  }
  onChange((prev: any) => {
    const otpArray = prev.split('');
    const lastValue = currentValue[currentValue.length - 1];
    otpArray[indexToEnter] = lastValue;
    return otpArray.join('');
  });
  if (currentValue !== '') {
    if (currentIndex < length - 1) {
      focusInput(currentIndex + 1);
    }
  }
};

  const handleClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>,
    currentIndex: number
  ) => {
    selectInput(currentIndex);
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    // Check if there is text data in the clipboard
    if (clipboardData.types.includes('text/plain')) {
      let pastedText = clipboardData.getData('text/plain');
      pastedText = pastedText.substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split('');

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? ' ';
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(''));
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
        <StyledBaseInput
          slots={{
            input: InputElement,
          }}
          type='number'
          aria-label={`Digit ${index + 1} of OTP`}
          slotProps={{
            input: {
              ref: (ele) => {
                inputRefs.current[index] = ele!;
              },
              onKeyDown: (event) => handleKeyDown(event, index),
              onChange: (event) => handleChange(event, index),
              onClick: (event) => handleClick(event, index),
              onPaste: (event) => handlePaste(event, index),
              value: value[index] ?? '',
            },
          }}
        />
        {!isMobile && index !== length - 1 && <span>-</span>}
        </React.Fragment>
      ))}
    </Box>
  );
};

const OTPInput: React.FC<Props> = ({ error = '', length = 6, otp, setOtp , onEnter}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        columnGap: 2,
        rowGap: 1
      }}
    >
      <OTP value={otp} onChange={setOtp} length={length} onEnter={onEnter}/>
      {error && <StyledError variant='body2'>{error}</StyledError>}
    </Box>
  );
};

export default OTPInput;

const blue = {
  100: '#DAECFF',
  200: '#80BFFF',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  700: '#0059B2',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const InputElement = styled('input')(
  ({ theme }) => `
  width: 40px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 0px;
  border-radius: 8px;
  text-align: center;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
  box-shadow: 0px 2px 4px ${
    theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'
    };

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${
      theme.palette.mode === 'dark' ? blue[600] : blue[200]
    };
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`
);
